import { useEffect, useState } from "react";
import { Cherry, Citrus, Bell, Star, Diamond, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Overlay } from "@/components/Overlay";
import { GameHeader, BetControls } from "@/components/games/GameShell";
import { api, errMsg, fmt, curText } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const ICONS = {
  cherry: [Cherry, "#F43F5E"], lemon: [Citrus, "#FACC15"], bell: [Bell, "#FB923C"],
  star: [Star, "#A78BFA"], diamond: [Diamond, "#22D3EE"], seven: [null, "#FFB800"],
};

const Sym = ({ s }) => {
  const [I, c] = ICONS[s];
  return I ? <I size={54} color={c} strokeWidth={2.2} /> : <span className="font-display text-6xl font-black" style={{ color: c }}>7</span>;
};

export function SlotModal({ game, onClose }) {
  const { currency, setUser } = useApp();
  const [bet, setBet] = useState(currency === "gc" ? 100 : 1);
  useEffect(() => { setBet(currency === "gc" ? 100 : 1); }, [currency, setBet]);
  const [reels, setReels] = useState(["seven", "diamond", "seven"]);
  const [spinning, setSpinning] = useState(false);
  const [win, setWin] = useState(null);

  const spin = async () => {
    setSpinning(true); setWin(null);
    try {
      const [{ data }] = await Promise.all([api.post("/games/spin", { game_id: game.id, currency, bet: Number(bet) }), new Promise((r) => setTimeout(r, 900))]);
      setReels(data.reels); setWin(data.win); setUser(data.user);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSpinning(false); }
  };

  return (
    <Overlay open={!!game} onClose={onClose} testId="slot-modal" className="max-w-xl">
      {game && (
        <div>
          <GameHeader game={game} />
          <div className="relative p-5 bg-gradient-to-b from-[#1A1030] via-[#120C24] to-[#0B0E14]">
            <div className="relative grid grid-cols-3 gap-3 rounded-2xl bg-[#07050F] p-3 border-2 border-[#FFB800]/40 shadow-[inset_0_0_40px_rgba(255,184,0,0.12),0_0_30px_rgba(255,184,0,0.15)]">
              {reels.map((s, i) => (
                <div key={i} data-testid={`reel-${i}`} className="h-28 rounded-xl bg-gradient-to-b from-[#241b3d] to-[#120c24] flex items-center justify-center overflow-hidden ring-1 ring-white/5">
                  <div className={spinning ? "reel-spinning" : ""}><Sym s={spinning ? Object.keys(ICONS)[(i + 2) % 6] : s} /></div>
                </div>
              ))}
            </div>
            <div className="h-10 mt-3 flex items-center justify-center font-display text-xl font-black" data-testid="spin-result">
              {win !== null && (win > 0 ? <span className={curText(currency)}>WIN {fmt(win, currency)} {currency.toUpperCase()}!</span> : <span className="text-gray-500 text-base">No win — try again</span>)}
            </div>
            <div className="mt-2"><BetControls bet={bet} setBet={setBet} disabled={spinning} /></div>
            <button data-testid="spin-btn" disabled={spinning} onClick={spin} className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-[#FFB800] text-[#0B0E14] py-4 font-display text-xl font-black hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-[filter,transform,opacity] shadow-[0_0_24px_rgba(255,184,0,0.4)]">
              {spinning ? <Loader2 className="animate-spin" /> : "SPIN"}
            </button>
          </div>
        </div>
      )}
    </Overlay>
  );
}
